import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { InputFileMetadata } from '../../../../generated.commonjs/api_pb';
import {
  ExecutiveSummarySection,
  FileComplianceSummary,
  FileOverviewSummary,
  FilePageNavigationStatus,
  SummaryAvailability,
} from '../../../../generated.commonjs/plan.reviewer_pb';
import { DocumentType, FileMetadataService, ProcessingStatus } from '../../../shared/file-metadata.service';
import { PlanReviewerService } from '../../../shared/plan-reviewer.service';

interface SummarySectionView {
  heading: string;
  icon: string;
  value?: ExecutiveSummarySection.AsObject;
}

/** Concise file metadata and persisted executive-summary projection. */
@Component({
  selector: 'app-file-overview',
  imports: [CommonModule, MatButtonModule, MatIconModule, MatProgressBarModule],
  templateUrl: './file-overview.component.html',
  styleUrls: ['./file-overview.component.scss'],
})
export class FileOverviewComponent implements OnChanges {
  private fileMetadataService = inject(FileMetadataService);
  private planReviewerService = inject(PlanReviewerService);
  private router = inject(Router);
  private loadGeneration = 0;

  @Input({ required: true }) projectId = '';
  @Input({ required: true }) fileId = '';
  @Output() pageStatusesChange = new EventEmitter<FilePageNavigationStatus.AsObject[]>();

  readonly ProcessingStatus = ProcessingStatus;
  metadata?: InputFileMetadata.AsObject;
  overview?: FileOverviewSummary.AsObject;
  compliance?: FileComplianceSummary.AsObject;
  metadataLoading = true;
  summaryLoading = true;
  metadataError = '';
  summaryError = '';

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['projectId'] || changes['fileId']) && this.projectId && this.fileId) {
      void this.load();
    }
  }

  get sections(): SummarySectionView[] {
    return [
      { heading: 'Document contents', icon: 'description', value: this.overview?.contents },
      { heading: 'Code applicability', icon: 'rule', value: this.overview?.applicabilityPosture },
      { heading: 'Compliance posture', icon: 'fact_check', value: this.overview?.compliancePosture },
    ];
  }

  get pageCount(): number {
    return this.metadata?.pageCount || this.compliance?.coverage?.filePageCount || 0;
  }

  get ingestedPageCount(): number {
    return this.metadata?.extractedPagesList?.length || this.compliance?.coverage?.ingestedPageCount || 0;
  }

  get complianceEligible(): boolean {
    return this.ingestedPageCount > 0;
  }

  formatDocumentType(value: DocumentType): string {
    return this.fileMetadataService.formatDocumentType(value);
  }

  formatFileSize(value: number): string {
    return this.fileMetadataService.formatFileSize(value);
  }

  formatProcessingStatus(value: ProcessingStatus): string {
    return this.fileMetadataService.formatProcessingStatus(value);
  }

  processingIcon(value: ProcessingStatus): string {
    switch (value) {
      case ProcessingStatus.PROCESSING_STATUS_COMPLETED:
        return 'check_circle';
      case ProcessingStatus.PROCESSING_STATUS_PROCESSING:
        return 'hourglass_top';
      case ProcessingStatus.PROCESSING_STATUS_FAILED:
        return 'error';
      case ProcessingStatus.PROCESSING_STATUS_UPLOADED:
        return 'cloud_upload';
      default:
        return 'help_outline';
    }
  }

  availabilityLabel(value?: ExecutiveSummarySection.AsObject): string {
    switch (value?.availability) {
      case SummaryAvailability.SUMMARY_AVAILABILITY_CURRENT:
        return 'Current';
      case SummaryAvailability.SUMMARY_AVAILABILITY_PARTIAL:
        return 'Partial';
      case SummaryAvailability.SUMMARY_AVAILABILITY_STALE:
        return 'Stale';
      default:
        return 'Unavailable';
    }
  }

  availabilityIcon(value?: ExecutiveSummarySection.AsObject): string {
    switch (value?.availability) {
      case SummaryAvailability.SUMMARY_AVAILABILITY_CURRENT:
        return 'check_circle';
      case SummaryAvailability.SUMMARY_AVAILABILITY_PARTIAL:
        return 'data_usage';
      case SummaryAvailability.SUMMARY_AVAILABILITY_STALE:
        return 'history';
      default:
        return 'info';
    }
  }

  sectionText(value?: ExecutiveSummarySection.AsObject): string {
    return value?.summary || 'This summary is not available from the current file evidence.';
  }

  coverageText(value?: ExecutiveSummarySection.AsObject): string {
    const source = value?.sourcePageCount ?? 0;
    const total = value?.filePageCount ?? this.pageCount;
    return `${source} of ${total} pages contributed`;
  }

  generatedDate(): Date | null {
    const seconds = this.overview?.generatedTime?.seconds;
    if (seconds === undefined || seconds === null) {
      return null;
    }
    return new Date(Number(seconds) * 1000);
  }

  openCompliance(): void {
    void this.router.navigate(['/projects', this.projectId, 'files', this.fileId, 'compliance']);
  }

  openTasks(): void {
    void this.router.navigate(['/projects', this.projectId, 'tasks']);
  }

  retrySummary(): void {
    void this.loadSummaries(this.loadGeneration);
  }

  private async load(): Promise<void> {
    const generation = ++this.loadGeneration;
    this.metadata = undefined;
    this.overview = undefined;
    this.compliance = undefined;
    this.metadataError = '';
    this.summaryError = '';
    this.metadataLoading = true;
    this.summaryLoading = true;
    void this.loadMetadata(generation);
    void this.loadSummaries(generation);
  }

  private async loadMetadata(generation: number): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.fileMetadataService.getFileMetadata(this.projectId, this.fileId, false)
      );
      if (generation !== this.loadGeneration) return;
      const metadata = response.getMetadata();
      if (!response.getFound() || !metadata) {
        throw new Error(response.getErrorMessage() || 'File metadata was not found.');
      }
      this.metadata = metadata.toObject();
    } catch {
      if (generation === this.loadGeneration) {
        this.metadataError = 'File metadata could not be loaded.';
      }
    } finally {
      if (generation === this.loadGeneration) {
        this.metadataLoading = false;
      }
    }
  }

  private async loadSummaries(generation: number): Promise<void> {
    this.summaryLoading = true;
    this.summaryError = '';
    const [overview, compliance] = await Promise.allSettled([
      this.planReviewerService.getFileOverviewSummary(this.projectId, this.fileId),
      this.planReviewerService.getFileComplianceSummary(this.projectId, this.fileId),
    ]);
    if (generation !== this.loadGeneration) return;
    if (overview.status === 'fulfilled') {
      this.overview = overview.value.toObject();
      this.pageStatusesChange.emit(this.overview.pageStatusesList);
    }
    if (compliance.status === 'fulfilled') {
      this.compliance = compliance.value.toObject();
    }
    if (overview.status === 'rejected' || compliance.status === 'rejected') {
      this.summaryError = 'Some file summary evidence could not be loaded.';
    }
    this.summaryLoading = false;
  }
}
